import { useSyncExternalStore } from "react";

/** Chromium's install event — not in the TS DOM lib. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Module-level capture: the browser fires beforeinstallprompt ONCE, usually
// before React mounts — a component-level listener would miss it.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
  notify();
});
window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  installedThisSession = true;
  notify();
});

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIOS(): boolean {
  // iPadOS 13+ reports itself as a Mac; touch support tells them apart.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

/** How the affordance should behave — or null to render nothing (already on
 *  the homescreen, just installed, or a browser with no install path). */
export type InstallMode = "prompt" | "ios-instructions" | null;

function getSnapshot(): InstallMode {
  if (installedThisSession || isStandalone()) return null;
  if (deferredPrompt) return "prompt";
  if (isIOS()) return "ios-instructions";
  return null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Chrome allows one prompt() per captured event; after use (accepted or
 *  dismissed) the affordance hides until the browser re-fires on a later
 *  visit. */
async function promptInstall(): Promise<void> {
  const p = deferredPrompt;
  if (!p) return;
  await p.prompt();
  deferredPrompt = null;
  notify();
}

export function useAddToHomescreen(): {
  mode: InstallMode;
  promptInstall: () => Promise<void>;
} {
  const mode = useSyncExternalStore(subscribe, getSnapshot);
  return { mode, promptInstall };
}
